import {
    BulleAide,
    FieldBirthplaceSelect,
    FieldCheckbox,
    FieldCountrySelect,
    FieldInput,
    FieldPatternInput,
    FieldSelectNative,
    IbanFields,
    Question,
    SmartButton,
} from '@workspace/core-ts/components';
import { Paiement, Permis } from '@workspace/core-ts/icons-amo';
import { isAdult, isAfter1900, isNotInTheFuture, RELATIONSHIP_OPTIONS } from '@workspace/utils';
import { addDays, addMinutes, format, isAfter, isSameDay, isValid, parse } from 'date-fns';
import DOMPurify from 'dompurify';
import { useEffect, useMemo } from 'react';
import { useMutationFill } from '../../api';
import { prelevementStepContent } from '../../content';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '../../contexts';
import { useAvenant, useContent } from '../../hooks';
import type { StepContentConfig } from '../../utils';
import { MOTIF_AVENANT } from '../../utils/avenant';
import { QuandDebuterContrat } from './quand-debuter-contrat';

const MOTIF_MISE_A_JOUR_DECLARATION = MOTIF_AVENANT.MISE_A_JOUR_DECLARATION;

interface PrelevementStepProps {
    content?: StepContentConfig;
    showConditions?: boolean;
    showHeaderIcon?: boolean;
    firstPaymentLabel?: string | ((paymentInfo: PaymentInfo) => string);
    enableTiersPayeur?: boolean;
}

interface PaymentInfo {
    formuleChoisie: string;
    fractionnement: string;
    taxeAttentat: number;
    montant: number;
}

/**
 * Retourne now + 15min formaté
 */
function getNowPlus15Min(): string {
    return format(addMinutes(new Date(), 15), 'dd/MM/yyyy HH:mm');
}

/**
 * Calcule la date d'effet pour un avenant motif 8 (mise à jour déclaration)
 */
function computeDateMiseAJour(dateEffetContrat: string, heureEffetContrat: string): string {
    const now = new Date();
    const dateEffet = parse(dateEffetContrat, 'dd/MM/yyyy', new Date());

    if (!isValid(dateEffet)) return format(now, 'dd/MM/yyyy HH:mm');

    // Même jour → lendemain
    if (isSameDay(dateEffet, now)) {
        return `${format(addDays(dateEffet, 1), 'dd/MM/yyyy')} ${heureEffetContrat}`;
    }

    // Dans le futur → cette date
    if (isAfter(dateEffet, now)) {
        return `${format(dateEffet, 'dd/MM/yyyy')} ${heureEffetContrat}`;
    }

    // Dans le passé → maintenant
    return format(now, 'dd/MM/yyyy HH:mm');
}

/**
 * Calcule la date d'effet pour un avenant standard
 */
function computeDateAvenant(dateEffetContrat: string | null): string {
    const nowPlus15 = addMinutes(new Date(), 15);

    if (!dateEffetContrat) return format(nowPlus15, 'dd/MM/yyyy HH:mm');

    const dateEffet = parse(dateEffetContrat, 'dd/MM/yyyy', new Date());

    // Si dateEffetContrat est dans le futur, on l'utilise avec l'heure actuelle + 15min
    if (isValid(dateEffet) && isAfter(dateEffet, nowPlus15)) {
        dateEffet.setHours(nowPlus15.getHours(), nowPlus15.getMinutes(), 0, 0);
        return format(dateEffet, 'dd/MM/yyyy HH:mm');
    }

    return format(nowPlus15, 'dd/MM/yyyy HH:mm');
}

export function PrelevementStep({
    content = prelevementStepContent,
    showConditions = true,
    showHeaderIcon = true,
    firstPaymentLabel,
    enableTiersPayeur = false,
}: PrelevementStepProps) {
    const methods = useSmartFormGlobal<any>();
    const { meta } = useGlobalForm();
    const mutationFill = useMutationFill(meta.token);
    const r = useContent();
    const isAvenant = useAvenant();

    // Valeurs du formulaire
    const motifAvenant = methods.watch('DemandeTarif.Police.MotifAvenant');
    const dateEffetContrat = methods.watch('Devis.Police.DateEffetContrat');
    const nomTitulaire = methods.watch('DemandeContrat.NomTitulaire');
    const souscripteur = methods.watch('DemandeTarif.ListePersonnes.0');
    const relationship = methods.watch('DemandeContrat.TiersPayeur.Relationship');

    const isMiseAJourDeclaration = isAvenant && motifAvenant === MOTIF_MISE_A_JOUR_DECLARATION;

    // Initialisation des valeurs par défaut au mount
    useEffect(() => {
        // Date d'effet
        if (!methods.getValues('DemandeContrat.DateHeureEffet')) {
            let initialDate: string;

            if (isMiseAJourDeclaration && dateEffetContrat) {
                const heureEffet = methods.getValues('DemandeTarif.Police.HeureEffetContrat') || '00:00';
                initialDate = computeDateMiseAJour(dateEffetContrat, heureEffet);
            } else if (isAvenant) {
                initialDate = computeDateAvenant(dateEffetContrat);
            } else {
                initialDate = getNowPlus15Min();
                methods.setValue('DemandeContrat.QuandDebuterContrat', '0');
            }

            methods.setValue('DemandeContrat.DateHeureEffet', initialDate);
        }

        // Pré-remplissage IBAN depuis les infos du souscripteur (affaire nouvelle uniquement)
        if (!isAvenant && souscripteur && !methods.getValues('DemandeContrat.NomTitulaire')) {
            methods.setValue('DemandeContrat.NomTitulaire', `${souscripteur.Nom || ''} ${souscripteur.Prenom || ''}`.trim());
            methods.setValue('DemandeContrat.adrIBAN', souscripteur.Adresse1);
            methods.setValue('DemandeContrat.cpIBAN', souscripteur.CP);
            methods.setValue('DemandeContrat.villeIBAN', souscripteur.Ville);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Infos de paiement pour la bulle d'aide (uniquement si firstPaymentLabel est fourni)
    const paymentInfo = useMemo<PaymentInfo | null>(() => {
        if (!firstPaymentLabel) return null;

        const Devis = methods.getValues('Devis');
        const formuleChoisie = methods.getValues('DemandeTarif.Police.FormuleChoisie');
        const fractionnement = methods.getValues('DemandeTarif.Police.FractionnementChoisi') || 'M';

        if (!Devis?.Formule?.MontantComptantPromo) return null;

        return {
            montant: Devis.Formule.MontantComptantPromo,
            fractionnement,
            formuleChoisie,
            taxeAttentat: Devis.Formule.MontantTaxeAttentat || 0,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstPaymentLabel]);

    // Afficher le tiers payeur si le titulaire du compte est différent du souscripteur
    const showTiersPayeur = useMemo(() => {
        if (!enableTiersPayeur || !nomTitulaire || !souscripteur) return false;

        const nomSouscripteur = `${souscripteur.Nom || ''} ${souscripteur.Prenom || ''}`.trim().toUpperCase();
        return nomTitulaire.trim().toUpperCase() !== nomSouscripteur;
    }, [enableTiersPayeur, nomTitulaire, souscripteur]);

    const handleSubmit = async (data: any) => {
        const response = await mutationFill.mutateAsync(data);
        return response?.data;
    };

    // Rendu simplifié pour les avenants
    if (isAvenant) {
        return (
            <SmartFormGlobal {...methods} className="mx-auto max-w-lg" onSubmit={handleSubmit}>
                <Question>
                    <Question.Header>
                        {showHeaderIcon && <Paiement />}
                        <Question.Title>{r(content.avenantTitle) || r(content.mainTitle)}</Question.Title>
                    </Question.Header>
                    <QuandDebuterContrat content={content} />
                </Question>
            </SmartFormGlobal>
        );
    }

    return (
        <SmartFormGlobal
            {...methods}
            className="mx-auto max-w-lg"
            onSubmit={handleSubmit}
        >
            {/* Question 1 : Informations de prélèvement */}
            <Question>
                <Question.Header>
                    {showHeaderIcon && <Paiement />}
                    <Question.Title>{r(content.mainTitle)}</Question.Title>
                </Question.Header>

                <QuandDebuterContrat content={content} />
                <IbanFields />

                {showConditions && (
                    <div className="space-y-3 pt-4">
                        <FieldCheckbox
                            name="DemandeContrat.AcceptationCG"
                            label={r(content.conditionsGeneralesLabel)}
                            className="self-start gap-3"
                            validation={{ required: "L'acceptation des documents est requise." }}
                        />
                    </div>
                )}

                {paymentInfo && firstPaymentLabel && (
                    <BulleAide
                        title="Information de prélèvement"
                        hideIcon
                    >
                        <p className="text-xs">
                            {typeof firstPaymentLabel === 'function' ? (
                                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(firstPaymentLabel(paymentInfo)) }} />
                            ) : (
                                firstPaymentLabel
                            )}
                        </p>
                    </BulleAide>
                )}
            </Question>

            {/* Question 2 : Tiers payeur (si le titulaire du compte est différent du souscripteur) */}
            <Question
                hideButton
                disabledAutoNext
                active={showTiersPayeur}
            >
                <Question.Header>
                    <Permis />
                    <Question.Title>Informations du tiers payeur</Question.Title>
                </Question.Header>

                <BulleAide hideIcon>
                    <p className="text-xs">
                        La personne qui va payer les cotisations n&apos;étant pas présente sur le contrat, nous avons besoin d&apos;avoir ses informations personnelles.
                    </p>
                </BulleAide>

                <FieldInput
                    label="Nom d'usage"
                    name="DemandeContrat.TiersPayeur.LastName"
                    type="text"
                    placeholder="Nom d'usage"
                    validation={{ required: "Le nom d'usage est requis" }}
                    normalize="uppercaseStrict"
                />

                <FieldInput
                    label="Nom de naissance"
                    name="DemandeContrat.TiersPayeur.BirthName"
                    type="text"
                    placeholder="Nom de naissance"
                    validation={{ required: 'Le nom de naissance est requis' }}
                    normalize="uppercaseStrict"
                />

                <FieldInput
                    label="Prénom"
                    name="DemandeContrat.TiersPayeur.FirstName"
                    type="text"
                    placeholder="Prénom"
                    validation={{
                        required: 'Le prénom est requis',
                        minLength: { value: 2, message: 'Le prénom doit contenir au moins 2 caractères' },
                    }}
                    normalize="uppercaseStrict"
                />

                <FieldPatternInput
                    label="Date de naissance"
                    name="DemandeContrat.TiersPayeur.BirthDate"
                    type="date"
                    validation={{
                        required: 'La date de naissance est requise',
                        validate: { isNotInTheFuture, isAdult, isAfter1900 },
                    }}
                />

                <FieldCountrySelect
                    name="DemandeContrat.TiersPayeur.BirthCountry"
                    label="Pays de naissance"
                    labelSearch="pays"
                    nameBirthCity="DemandeContrat.TiersPayeur.BirthCity"
                    nameBirthDepartment="DemandeContrat.TiersPayeur.BirthDepartment"
                />

                <FieldBirthplaceSelect
                    nameBirthplace="DemandeContrat.TiersPayeur.BirthCity"
                    nameCountry="DemandeContrat.TiersPayeur.BirthCountry"
                    nameDepartment="DemandeContrat.TiersPayeur.BirthDepartment"
                />

                <FieldSelectNative
                    label="Lien souscripteur / payeur"
                    name="DemandeContrat.TiersPayeur.Relationship"
                    options={RELATIONSHIP_OPTIONS}
                    placeholder="Sélectionnez..."
                    validation={{ required: 'Le lien avec le souscripteur est requis' }}
                />

                {relationship === 'A' && (
                    <FieldInput
                        label="Motif d'intervention du payeur"
                        name="DemandeContrat.TiersPayeur.ThirdPayerReason"
                        type="text"
                        placeholder="Précisez le motif"
                        validation={{ required: "Le motif d'intervention est requis" }}
                    />
                )}

                <SmartButton loading={mutationFill.isPending}>{r(content.buttonText)}</SmartButton>
            </Question>
        </SmartFormGlobal>
    );
}
